/**
 * 13-bar rainbow xylophone (C4–A5, C major diatonic).
 * Same input rules as the piano: pointerdown + glissando + multi-touch.
 */

import { createSignal, For } from "solid-js";
import { SAMPLE_MANIFEST, type SoundKey } from "@melodimals/game-core";
import { cn } from "@/lib/cn";
import { useAudio } from "@/providers/audio-provider";

const BAR_COLORS = [
  "bg-red-400",
  "bg-orange-400",
  "bg-amber-400",
  "bg-yellow-400",
  "bg-lime-400",
  "bg-green-400",
  "bg-emerald-400",
  "bg-teal-400",
  "bg-sky-400",
  "bg-blue-400",
  "bg-indigo-400",
  "bg-violet-400",
  "bg-fuchsia-400",
];

interface XylophoneBarsProps {
  highlightedKey?: SoundKey | null;
  onNotePlayed?: (key: SoundKey) => void;
  class?: string;
}

export function XylophoneBars(props: XylophoneBarsProps) {
  const audio = useAudio();
  const [pressed, setPressed] = createSignal<ReadonlySet<SoundKey>>(new Set());
  const activePointers = new Set<number>();
  const keys = SAMPLE_MANIFEST.xylophone;

  const press = (key: SoundKey) => {
    audio.playNote("xylophone", key);
    props.onNotePlayed?.(key);
    setPressed((prev) => new Set(prev).add(key));
  };

  const release = (key: SoundKey) => {
    setPressed((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  return (
    <div
      class={cn("flex h-full w-full items-center gap-1.5 select-none", props.class)}
      style={{ "touch-action": "none" }}
    >
      <For each={keys}>
        {(key, i) => (
          <button
            type="button"
            tabIndex={-1}
            aria-label={key}
            onPointerDown={(e) => {
              e.preventDefault();
              activePointers.add(e.pointerId);
              press(key);
            }}
            onPointerEnter={(e) => {
              if (activePointers.has(e.pointerId)) {
                press(key);
              }
            }}
            onPointerUp={(e) => {
              activePointers.delete(e.pointerId);
              release(key);
            }}
            onPointerLeave={() => {
              release(key);
            }}
            onPointerCancel={(e) => {
              activePointers.delete(e.pointerId);
              release(key);
            }}
            class={cn(
              "min-w-0 flex-1 rounded-full border-2 border-black/15 shadow-md transition-transform duration-75",
              BAR_COLORS[i() % BAR_COLORS.length],
              pressed().has(key) && "scale-95 brightness-110",
              props.highlightedKey === key && "animate-pulse ring-4 ring-white",
            )}
            style={{
              // Decreasing bars: from 100% (low) to 55% (high).
              height: `${100 - (i() / (keys.length - 1)) * 45}%`,
            }}
          />
        )}
      </For>
    </div>
  );
}
